import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowLeft, MessageCircle, Clock, Headphones } from "lucide-react";
import api, { fcfa } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function BookingPayment() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [whatsapp, setWhatsapp] = useState("");

  const loadBooking = useCallback(() => {
    api.get(`/bookings/${bookingId}`).then(({ data }) => setBooking(data)).catch(() => navigate("/client"));
  }, [bookingId, navigate]);

  useEffect(() => {
    loadBooking();
    api.get("/config").then(({ data }) => setWhatsapp(data.whatsapp_number)).catch(() => {});
  }, [loadBooking]);

  if (!booking) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  if (booking.status === "payee") {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-5" />
        <h1 className="font-head text-3xl font-extrabold text-primary mb-2">Réservation payée !</h1>
        <p className="text-muted-foreground mb-6">Votre paiement pour « {booking.offer_title} » est confirmé.</p>
        <Button onClick={() => navigate("/client")} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="back-to-dashboard-btn">
          Voir mes réservations
        </Button>
      </div>
    );
  }

  const message = encodeURIComponent(
    `Bonjour WORLD TRAVEL, je souhaite payer ma réservation :\n` +
    `• Offre : ${booking.offer_title}\n` +
    `• Destination : ${booking.destination}\n` +
    `• Date : ${new Date(booking.date).toLocaleDateString("fr-FR")}\n` +
    `• Personnes : ${booking.num_people}\n` +
    `• Montant total : ${fcfa(booking.total_amount)}\n` +
    `• Référence : ${booking.id}`
  );
  const waLink = whatsapp ? `https://wa.me/${whatsapp}?text=${message}` : "#";

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <button onClick={() => navigate("/client")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-primary" data-testid="payment-back-btn">
        <ArrowLeft className="h-4 w-4" /> Retour
      </button>

      <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-5 mb-6">
        <h1 className="font-head text-2xl font-extrabold text-primary mb-1">Finaliser le paiement</h1>
        <p className="text-muted-foreground text-sm">{booking.offer_title} — {booking.num_people} personne(s)</p>
        <div className="flex items-center justify-between mt-4 bg-secondary rounded-xl p-4">
          <span className="font-semibold text-primary">Montant total</span>
          <span className="font-head text-2xl font-extrabold text-accent" data-testid="payment-total">{fcfa(booking.total_amount)}</span>
        </div>
        <div className="flex items-center gap-2 mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="payment-pending-note">
          <Clock className="h-4 w-4 shrink-0" /> Statut : en attente de paiement
        </div>
      </div>

      <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-6 text-center">
        <span className="h-14 w-14 rounded-2xl bg-[#25D366]/15 flex items-center justify-center mx-auto mb-4">
          <Headphones className="h-7 w-7 text-[#25D366]" />
        </span>
        <h2 className="font-head text-xl font-extrabold text-primary mb-2">Contactez le support pour payer</h2>
        <p className="text-muted-foreground text-sm mb-6">
          Pour finaliser votre réservation, contactez notre équipe sur WhatsApp. Nous vous communiquerons les modalités de paiement et confirmerons votre réservation une fois le règlement reçu.
        </p>
        {whatsapp ? (
          <a href={waLink} target="_blank" rel="noreferrer" data-testid="contact-support-pay-btn">
            <Button className="w-full h-12 rounded-full bg-[#25D366] hover:bg-[#1da851] text-white font-bold">
              <MessageCircle className="mr-2 h-5 w-5" /> Contacter le support sur WhatsApp
            </Button>
          </a>
        ) : (
          <p className="text-sm text-muted-foreground">Contact indisponible pour le moment.</p>
        )}
        <p className="text-xs text-muted-foreground mt-4">
          Votre réservation reste enregistrée. Vous retrouverez son statut dans « Mes réservations ».
        </p>
      </div>
    </div>
  );
}
